import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import React, {useMemo} from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {SequenceSchemaType, TagSchemaSupportedFolderItemType} from 'constants/RegistryConstants';
import {TagSchemaSequenceSchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  COMPLEX_POLYMER_DISPLAY_NAMES,
  COMPLEX_POLYMER_TYPE_TO_DESCRIPTION,
  ComplexPolymerType,
  getSupportedPolymerTypes,
} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {FONT_PROXIMA_12, FONT_PROXIMA_13} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_DARK, GRAY_DARKER, NEAR_BLACK} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {capitalize} from 'util/NameUtil';
import {InputChangeEvent} from 'util/Types';

import {TagSchemaSummaryGraphQL} from '../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaSummaryGraphQL';
import {
  ComplexPolymerInterfaceApiId,
  complexPolymerTypeToSchemaInterfaceId,
  DraftImplementedComplexPolymerInterface,
  implementedInterfaceToComplexPolymerType,
  isComplexPolymerImplementedInterface,
  isOligoConjugateImplementedInterface,
  isOligoDuplexImplementedInterface,
  UNSET_COMPLEX_POLYMER_INTERFACE_ID,
} from './ComplexPolymerSchemaInterfaceConstants';
import OligoConjugateConfigurationTable from './oligo-conjugates/OligoConjugateConfigurationTable';
import OligoDuplexConfigurationTable from './oligo-duplexes/OligoDuplexConfigurationTable';

export const EXTENDED_CONJUGATE_FOLDER_ITEM_TYPES: Array<
  TagSchemaSupportedFolderItemType | SequenceSchemaType
> = [
  ModelTypeConstants.MOLECULE,
  TagSchemaSequenceSchemaType.RNA_OLIGO,
  TagSchemaSequenceSchemaType.OLIGO,
  TagSchemaSequenceSchemaType.RNA_SEQUENCE,
];

function ComplexPolymerConfigurationCheckbox(props: {
  checked: boolean;
  disabled: boolean;
  onChange: (e: InputChangeEvent) => void;
}): JSX.Element {
  return (
    <CheckboxInput
      className="u-tiny-spacing-bottom"
      id="configure-complex-polymer-checkbox"
      label="Configure as complex polymer"
      checked={props.checked}
      onChange={props.onChange}
      disabled={props.disabled}
      dataTestAttrs={{component: 'ComplexPolymerConfigurationCheckbox', element: 'checkboxInput'}}
    />
  );
}

function ComplexPolymerTypeOption(props: {
  type: ComplexPolymerType;
  description: string | null;
}): JSX.Element {
  const name = COMPLEX_POLYMER_DISPLAY_NAMES[props.type];
  return (
    <div>
      <span className={css(styles.typeName)}>{capitalize(name)} </span>
      {props.description && <div className={css(styles.typeDescription)}>{props.description}</div>}
    </div>
  );
}

interface ComplexPolymerTypeDropdownProps {
  disabled: boolean;
  onSelect: (value: ComplexPolymerType) => void;
  selectedType: ComplexPolymerType | null;
  complexPolymerTypes: Array<ComplexPolymerType>;
}

function ComplexPolymerTypeDropdown(props: ComplexPolymerTypeDropdownProps): JSX.Element {
  const complexPolymerTypeMenu = (
    <Menu scrollable={true} width={Menu.WIDTHS.FIT_CONTENT} height={Menu.HEIGHTS.MEDIUM}>
      {props.complexPolymerTypes.map((t) => (
        <Option
          key={t}
          value={t}
          onSelect={props.onSelect}
          dataTestAttrs={{component: 'ComplexPolymerTypeDropdown', element: 'typeOption', key: t.toString()}}
        >
          <ComplexPolymerTypeOption type={t} description={COMPLEX_POLYMER_TYPE_TO_DESCRIPTION[t]} />
        </Option>
      ))}
    </Menu>
  );
  return (
    <SelectDropdown
      className={css(styles.complexPolymerTypeSelector)}
      displayName={
        props.selectedType != null ? (
          <ComplexPolymerTypeOption type={props.selectedType} description={null} />
        ) : (
          <div className={css(styles.placeholder)}>Select type</div>
        )
      }
      menu={complexPolymerTypeMenu}
      disabled={props.disabled}
      dataTestAttrs={{component: 'ComplexPolymerTypeDropdown', element: 'selectDropdown'}}
    />
  );
}

export interface ComplexPolymerSchemaConfigurationProps {
  isWritable: boolean;
  complexPolymerConfig: DraftImplementedComplexPolymerInterface | null;
  onUpdateComplexPolymerConfig: (
    complexPolymerConfiguration: DraftImplementedComplexPolymerInterface | null
  ) => void;
  schemas: Array<TagSchemaSummaryGraphQL>;
}

export default function ComplexPolymerSchemaConfiguration(
  props: ComplexPolymerSchemaConfigurationProps
): JSX.Element {
  const isComplexPolymer =
    props.complexPolymerConfig != null && isComplexPolymerImplementedInterface(props.complexPolymerConfig);
  const selectedType = implementedInterfaceToComplexPolymerType(props.complexPolymerConfig);
  const complexPolymerTypes = useMemo(() => getSupportedPolymerTypes(), []);

  function onToggleConfiguration(): void {
    if (props.complexPolymerConfig == null) {
      props.onUpdateComplexPolymerConfig({interfaceId: UNSET_COMPLEX_POLYMER_INTERFACE_ID});
    } else {
      props.onUpdateComplexPolymerConfig(null);
    }
  }

  function onSetComplexPolymerType(newType: ComplexPolymerType | null): void {
    if (newType == null) {
      props.onUpdateComplexPolymerConfig({
        interfaceId: UNSET_COMPLEX_POLYMER_INTERFACE_ID,
      });
    } else if (newType === ComplexPolymerType.OLIGO_DUPLEX) {
      props.onUpdateComplexPolymerConfig({
        interfaceId: ComplexPolymerInterfaceApiId.OLIGO_DUPLEX_ID,
        conjugatesSchemaFieldSpecs: [],
      });
    } else {
      const interfaceId = complexPolymerTypeToSchemaInterfaceId(newType);
      assert(
        interfaceId === ComplexPolymerInterfaceApiId.OLIGO_CONJUGATE_ID,
        'Expected oligo conjugate interface'
      );
      props.onUpdateComplexPolymerConfig({interfaceId: ComplexPolymerInterfaceApiId.OLIGO_CONJUGATE_ID});
    }
  }

  return (
    <div {...getTestProps({component: 'ComplexPolymerSchemaConfiguration', element: 'root'})}>
      <div>
        <ComplexPolymerConfigurationCheckbox
          onChange={onToggleConfiguration}
          checked={isComplexPolymer}
          disabled={!props.isWritable}
        />
        {isComplexPolymer && (
          <ComplexPolymerTypeDropdown
            disabled={!props.isWritable}
            onSelect={onSetComplexPolymerType}
            selectedType={selectedType}
            complexPolymerTypes={complexPolymerTypes}
          />
        )}
      </div>
      {props.complexPolymerConfig != null && (
        <div className="u-moderate-spacing-top">
          {isOligoConjugateImplementedInterface(props.complexPolymerConfig) && (
            <OligoConjugateConfigurationTable
              oligoConjugateInterfaceImplementation={props.complexPolymerConfig}
              disabled={!props.isWritable}
              onChange={props.onUpdateComplexPolymerConfig}
              schemas={props.schemas}
            />
          )}
          {isOligoDuplexImplementedInterface(props.complexPolymerConfig) && (
            <OligoDuplexConfigurationTable
              oligoDuplexImplementedInterface={props.complexPolymerConfig}
              disabled={!props.isWritable}
              onChange={props.onUpdateComplexPolymerConfig}
              schemas={props.schemas}
            />
          )}
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  complexPolymerTypeSelector: {
    width: '50%',
  },
  placeholder: {
    ...FONT_PROXIMA_12,
    color: GRAY_DARKER,
  },
  typeName: {
    ...FONT_PROXIMA_13,
    color: NEAR_BLACK,
  },
  typeDescription: {
    ...FONT_PROXIMA_12,
    color: GRAY_DARK,
    marginTop: '2px',
    font: 'proxima-11',
  },
});
